import { useMemo } from 'react'

import { useProductAttributes } from './useProductAttributes'

import type { ProductState, CustomAttribute } from '../../types'

export interface ProductStateValue extends ProductState {
  description: string | null
  customAttributes: CustomAttribute[] | null
}

/**
 * Composition hook for product state
 * Combines product attributes data with derived state
 *
 * IMPORTANT: useMemo is REQUIRED here because this hook's return value is used
 * directly as Provider context value in ProductProvider. Without useMemo,
 * every render creates a new object reference, causing all context consumers to re-render.
 */
export function useProductState(productSku: string): ProductStateValue {
  const query = useProductAttributes(productSku)

  return useMemo(() => {
    const product = query.data || null
    const description = product?.description?.html || null
    const customAttributes = product?.custom_attributes || null

    return {
      product,
      description,
      customAttributes,
      loading: query.isLoading,
      error: query.error,
    }
  }, [query.data, query.error, query.isLoading])
}
